import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'

gsap.registerPlugin(ScrollToPlugin)

/** Read per call, not cached: the OS setting can change while the app is open. */
export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** The theme's own `sm`, in rem, used only if the custom property cannot be read. */
const SM_BREAKPOINT_FALLBACK_REM = 40

/**
 * Below Tailwind's `sm`, read off `--breakpoint-sm` rather than a hard-coded 640px. The
 * theme declares it in `rem`, so a px query disagrees at any root font size but 16px.
 *
 * Compared against `innerWidth` rather than `matchMedia`, which avoids the fractional
 * widths a zoomed page reports.
 */
const isBelowSmBreakpoint = (): boolean => {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') {
    return false
  }
  const root = document.documentElement
  const rootStyle = getComputedStyle(root)
  const rootFontSize = parseFloat(rootStyle.fontSize) || 16
  const declared = rootStyle.getPropertyValue('--breakpoint-sm').trim()
  const value = parseFloat(declared)
  const breakpointPx = Number.isNaN(value)
    ? SM_BREAKPOINT_FALLBACK_REM * rootFontSize
    : declared.endsWith('rem')
      ? value * rootFontSize
      : value
  return window.innerWidth < breakpointPx
}

/** Collapse/expand timings in seconds. The two directions run on separate
 *  sibling elements, so they cannot share a timeline; these numbers sequence
 *  the handoff instead. Fading opacity and height over the same window
 *  cross-dissolves the two states and reads as a smear. */
export const EXPAND_HEIGHT_DURATION = 0.22
const COLLAPSE_HEIGHT_DURATION = 0.18
const FADE_OUT_DURATION = 0.08
const FADE_IN_DURATION = 0.14
/** Slightly past `FADE_OUT_DURATION`, so the outgoing content is fully gone
 *  before the incoming content starts showing rather than meeting it midway. */
const FADE_IN_DELAY = 0.09

/**
 * The transition running on a collapsible region, so re-entering mid-flight
 * retires it as one piece. Killing two loose tweens can leave one alive, which
 * stranded regions at full height with zero opacity: a question collapsed into
 * a blank gap instead of its summary.
 */
const runningTransitions = new WeakMap<HTMLElement, gsap.core.Timeline>()

export const animateExpand = (el: HTMLElement, open: boolean): void => {
  runningTransitions.get(el)?.kill()
  runningTransitions.delete(el)
  if (prefersReducedMotion()) {
    setExpandState(el, open)
    return
  }
  const timeline = gsap.timeline({
    onComplete: () => runningTransitions.delete(el),
  })
  runningTransitions.set(el, timeline)
  if (open) {
    gsap.set(el, { height: 'auto', overflow: 'hidden' })
    const targetHeight = el.scrollHeight
    gsap.set(el, { height: 0 })
    timeline
      .to(
        el,
        {
          height: targetHeight,
          duration: EXPAND_HEIGHT_DURATION,
          ease: 'power1.out',
          onComplete: () =>
            gsap.set(el, { height: 'auto', overflow: 'visible' }),
        },
        0
      )
      .to(
        el,
        {
          autoAlpha: 1,
          duration: FADE_IN_DURATION,
          ease: 'power1.out',
        },
        FADE_IN_DELAY
      )
  } else {
    gsap.set(el, { height: el.scrollHeight, overflow: 'hidden' })
    timeline
      .to(
        el,
        {
          height: 0,
          duration: COLLAPSE_HEIGHT_DURATION,
          ease: 'power1.inOut',
        },
        0
      )
      .to(
        el,
        {
          autoAlpha: 0,
          duration: FADE_OUT_DURATION,
          ease: 'power1.in',
        },
        0
      )
  }
}

/** The end state with no animation, for the first render. */
export const setExpandState = (el: HTMLElement, open: boolean): void => {
  gsap.set(
    el,
    open
      ? { height: 'auto', autoAlpha: 1, overflow: 'visible' }
      : { height: 0, autoAlpha: 0, overflow: 'hidden' }
  )
}

const SCROLL_DURATION = 0.6
const SCROLL_OFFSET_Y = 24

const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown'] as const

/** Keys that scroll. A `keydown` outside this set is someone typing. */
const SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
])

/**
 * Smooth-scroll a completed section's summary card into view. Walks to the
 * nearest scrollable ancestor, since this app scrolls inside fixed-height
 * panels rather than the window.
 *
 * Refused on a phone, and abandoned on the user's own scroll: the card is
 * taller than `SCROLL_OFFSET_Y` leaves room for there, and writing scroll
 * position every frame fights a reader already scrolling.
 */
export const scrollIntoView = (el: HTMLElement): void => {
  if (isBelowSmBreakpoint()) return
  const duration = prefersReducedMotion() ? 0 : SCROLL_DURATION
  const removeListeners: Array<() => void> = []
  const stop = (): void => {
    while (removeListeners.length) removeListeners.pop()?.()
  }
  const tween = gsap.to(getScrollParent(el), {
    duration,
    scrollTo: { y: el, offsetY: SCROLL_OFFSET_Y },
    ease: 'power2.out',
    // Both exits, so a tween GSAP overwrites leaves no listener behind.
    onComplete: stop,
    onInterrupt: stop,
  })
  if (duration === 0) return
  const yieldToUser = (event: Event): void => {
    if (event instanceof KeyboardEvent && !SCROLL_KEYS.has(event.key)) return
    tween.kill()
    stop()
  }
  for (const type of USER_SCROLL_EVENTS) {
    window.addEventListener(type, yieldToUser, { passive: true })
    removeListeners.push(() => window.removeEventListener(type, yieldToUser))
  }
}

const getScrollParent = (el: HTMLElement): HTMLElement | Window => {
  let parent = el.parentElement
  while (parent) {
    const style = getComputedStyle(parent)
    const scrollableY = /(auto|scroll)/.test(style.overflowY)
    if (scrollableY && parent.scrollHeight > parent.clientHeight) {
      return parent
    }
    parent = parent.parentElement
  }
  return window
}

export const REVEAL_DURATION = 0.4

/** A newly-revealed question's entrance. `clearProps: 'transform'` matters:
 *  the inline transform GSAP leaves at y:0 creates a stacking context, letting
 *  a later question paint over an earlier one's open date-picker popover. */
export const animateReveal = (el: HTMLElement): void => {
  if (prefersReducedMotion()) {
    gsap.set(el, { autoAlpha: 1, y: 0, clearProps: 'transform' })
    return
  }
  gsap.fromTo(
    el,
    { autoAlpha: 0, y: 12 },
    {
      autoAlpha: 1,
      y: 0,
      duration: REVEAL_DURATION,
      ease: 'power2.out',
      clearProps: 'transform',
    }
  )
}
